import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Globe2, MapPin, Layers, Search, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { REGIONS, SUB_REGIONS } from '../mock/locations';
import { SERVICES_KEYWORDS, LANG_LABELS } from '../mock/serviceKeywords';
import RealWorldMap from './RealWorldMap';

const PANELS = [
  { id: 'coverage', icon: 'Globe2', label: 'Service Coverage', sub: 'Regions & countries served' },
  { id: 'cities', icon: 'MapPin', label: 'Cities We Serve', sub: 'Metro + regional markets worldwide' },
  { id: 'languages', icon: 'Search', label: 'Services in Every Language', sub: 'Multilingual SEO across 20 languages' }
];

const ICON_MAP = { Globe2, MapPin, Search };

const GlobalReachSection = () => {
  const [active, setActive] = useState('coverage');
  const allCities = REGIONS.flatMap(r => r.countries.flatMap(c => c.cities.map(city => ({ city, country: c.name, region: r.name }))));
  const langCodes = Object.keys(LANG_LABELS);

  return (
    <section
      data-testid="global-reach-section"
      className="relative py-20 bg-gradient-to-br from-amber-50 via-white to-gray-50 overflow-hidden"
      aria-labelledby="global-reach-heading"
    >
      <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(148,163,184,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.10)_1px,transparent_1px)] [background-size:48px_48px] pointer-events-none"></div>
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-orange-500 font-bold text-[12px] uppercase tracking-[0.25em] mb-3 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Worldwide</div>
          <h2 id="global-reach-heading" className="text-4xl md:text-5xl font-black text-[#0e1116]">
            Global Reach. <span className="text-green-600">Local</span> <span className="text-orange-500">Delivery.</span>
          </h2>
          <p className="text-gray-600 mt-3">One unified team, working across regions, cities and 20 languages — closer than your nearest local office.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* LEFT — Global Reach map */}
          <div data-testid="global-reach-map" className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe2 className="w-5 h-5 text-green-600" />
                <h3 className="font-black text-[#0e1116]">Global Reach</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded">Live coverage map</span>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-100 bg-gradient-to-br from-gray-50 to-white">
              <RealWorldMap compact />
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { v: '40+', l: 'Countries' },
                { v: '5', l: 'Continents' },
                { v: '24/7', l: 'Support' }
              ].map(s => (
                <div key={s.l} className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-lg p-3 text-center">
                  <div className="font-black text-[#0e1116] text-2xl leading-none">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — 3 dropdowns + always-visible keyword block to fill remaining space */}
          <div className="flex flex-col gap-3" data-testid="global-reach-panels">
            {PANELS.map(p => {
              const Ic = ICON_MAP[p.icon] || Globe2;
              const open = active === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`global-panel-${p.id}`}
                  className={`bg-white border rounded-xl overflow-hidden transition-all ${open ? 'border-gray-500 shadow-lg shadow-gray-500/10' : 'border-gray-200 hover:border-green-300'}`}
                >
                  <button
                    onClick={() => setActive(open ? null : p.id)}
                    aria-expanded={open}
                    data-testid={`global-panel-toggle-${p.id}`}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${open ? 'bg-gray-600 text-white' : 'bg-gray-100 text-green-700'} transition-colors`}>
                        <Ic className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-bold text-[#0e1116]">{p.label}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{p.sub}</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-green-600 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  {open && p.id === 'coverage' && (
                    <div className="px-5 pb-5 border-t border-gray-100" data-testid="panel-content-coverage">
                      <div className="grid sm:grid-cols-2 gap-3 mt-4 max-h-[300px] overflow-y-auto pr-1">
                        {Object.entries(SUB_REGIONS).map(([region, countries]) => (
                          <div key={region} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg p-3 hover:border-green-400">
                            <div className="flex items-center gap-2 mb-2">
                              <Globe2 className="w-3.5 h-3.5 text-green-600" />
                              <h4 className="font-bold text-[#0e1116] text-sm">{region}</h4>
                              <span className="ml-auto text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">{countries.length}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {countries.map(c => (
                                <span key={c} className="text-[10px] bg-gray-50 text-green-700 border border-gray-200 px-1.5 py-0.5 rounded">{c}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {open && p.id === 'cities' && (
                    <div className="px-5 pb-5 border-t border-gray-100" data-testid="panel-content-cities">
                      <div className="text-[11px] text-gray-500 mt-3 mb-2">Showing <span className="font-bold text-green-700">{allCities.length}</span> cities across all delivery regions.</div>
                      <div className="flex flex-wrap gap-1.5 max-h-[280px] overflow-y-auto pr-1">
                        {allCities.map((entry) => (
                          <span
                            key={`${entry.city}-${entry.country}`}
                            title={`${entry.country} • ${entry.region}`}
                            className="text-[11px] bg-gradient-to-r from-gray-50 to-white border border-gray-200 text-gray-700 px-2 py-1 rounded hover:from-gray-50 hover:to-gray-50 hover:border-green-400 hover:text-green-700 transition-all cursor-default"
                          >
                            {entry.city}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {open && p.id === 'languages' && (
                    <div className="px-5 pb-5 border-t border-gray-100" data-testid="panel-content-languages">
                      <div className="flex items-center justify-between text-[11px] text-gray-500 mt-3 mb-3">
                        <span><Layers className="w-3 h-3 inline mr-1" /> {SERVICES_KEYWORDS.length} services × {langCodes.length} languages</span>
                        <Link to="/services" className="text-green-600 hover:text-orange-500 font-bold uppercase tracking-wider">View all →</Link>
                      </div>
                      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                        {SERVICES_KEYWORDS.slice(0, 8).map(s => (
                          <details key={s.slug} className="bg-gray-50 border border-gray-200 rounded p-2 hover:border-green-300">
                            <summary className="text-[12px] font-bold text-[#0e1116] cursor-pointer flex items-center justify-between">
                              <span>{s.name}</span>
                              <ChevronRight className="w-3 h-3 text-green-600" />
                            </summary>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {langCodes.map(lc => (
                                <span
                                  key={lc}
                                  dir={lc === 'ar' ? 'rtl' : 'ltr'}
                                  title={LANG_LABELS[lc]}
                                  className="text-[10px] bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded"
                                >
                                  <span className="text-orange-500 font-bold mr-1">{lc.toUpperCase()}</span>{s.t[lc] || s.t.en}
                                </span>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* ALWAYS-VISIBLE keyword + capability block — fills empty space when accordions are collapsed */}
            <div
              data-testid="global-keyword-card"
              className="relative flex-1 min-h-[260px] bg-gradient-to-br from-[#0e1116] via-[#16202c] to-[#0e1116] text-white rounded-xl overflow-hidden border border-gray-700/30 shadow-lg"
            >
              <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:36px_36px] pointer-events-none"></div>
              <div className="absolute -top-12 -right-12 w-44 h-44 bg-orange-500/15 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-16 -left-16 w-52 h-52 bg-gray-500/15 rounded-full blur-3xl pointer-events-none"></div>

              <div className="relative p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-orange-400" />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-orange-300 font-bold">What we are known for</span>
                </div>
                <h4 className="text-lg md:text-xl font-black leading-tight">
                  CAD drafting, BIM &amp; engineering — <span className="text-green-400">28 services</span>,
                  <span className="text-orange-400"> 40+ countries</span>, one trusted partner.
                </h4>

                {/* Animated keyword chip cloud */}
                <div className="flex flex-wrap gap-1.5 mt-4" data-testid="global-keyword-chips">
                  {[
                    'CAD Drafting', 'BIM Modeling', 'Civil 3D', 'Revit', 'AutoCAD', 'Tekla',
                    'Land Surveying CAD Drafting', 'ALTA/NSPS', 'Topographic', 'Boundary', 'GIS Mapping',
                    'MEP Engineering', 'Structural Steel', 'Plant 3D', 'P&ID', 'Architectural',
                    '3D Modeling', 'Rendering', 'Scan-to-BIM', 'Digital Twin', 'Shop Drawings'
                  ].map((k, i) => (
                    <span
                      key={k}
                      className="text-[10px] bg-gray-900/40 border border-gray-600/40 text-green-100 px-2.5 py-1 rounded-full hover:bg-orange-500/30 hover:border-orange-400 hover:text-white transition-all cursor-default"
                      style={{ animation: `kw-pop 6s ${i * 0.18}s ease-in-out infinite`, opacity: 0.92 }}
                    >
                      <span className="inline-block w-1 h-1 rounded-full bg-orange-400 mr-1.5 align-middle"></span>{k}
                    </span>
                  ))}
                </div>

                {/* Capability mini-grid */}
                <div className="grid grid-cols-3 gap-2 mt-5" data-testid="global-cap-grid">
                  {[
                    { v: '15K+',  l: 'Projects' },
                    { v: '350+',  l: 'Engineers' },
                    { v: '20+',   l: 'Yrs Experience' },
                    { v: '99%',   l: 'On-Time' },
                    { v: '24/7',  l: 'Support' },
                    { v: 'ISO',   l: '9001:2015' }
                  ].map(s => (
                    <div key={s.l} className="bg-[#0a1a2a]/70 backdrop-blur-sm border border-gray-700/30 rounded p-2.5 text-center">
                      <div className="font-black text-orange-400 text-base leading-none">{s.v}</div>
                      <div className="text-[9px] uppercase tracking-wider text-green-200 mt-1">{s.l}</div>
                    </div>
                  ))}
                </div>

                <Link
                  to="/get-quote"
                  data-testid="global-kw-cta"
                  className="inline-flex items-center gap-2 mt-5 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded uppercase text-[11px] tracking-wider transition-colors"
                >
                  Start your project <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <style>{`
                @keyframes kw-pop {
                  0%, 100% { transform: translateY(0); opacity: 0.92; }
                  50%      { transform: translateY(-2px); opacity: 1; }
                }
              `}</style>
            </div>
          </div>
        </div>

        {/* SEO sr-only block (kept from old component) */}
        <div className="sr-only" aria-hidden="false">
          <h3>Multilingual Engineering Service Keywords</h3>
          {SERVICES_KEYWORDS.map(s => (
            <p key={s.slug}>
              <strong>{s.name}:</strong> {Object.values(s.t).join(' · ')}. Sub-services: {s.subServices.join(', ')}.
            </p>
          ))}
        </div>

        {/* IMPORTANT HIGHLIGHTS — fills the previously empty space */}
        <div className="mt-12 pt-10 border-t border-gray-200/60" data-testid="global-highlights">
          <div className="text-center mb-8">
            <div className="text-orange-500 font-bold text-[11px] uppercase tracking-[0.25em] mb-2">Why teams across 40+ countries trust us</div>
            <h3 className="text-2xl md:text-3xl font-black text-[#0e1116]">Important <span className="text-green-600">Highlights</span> Across the Website</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { ic: 'BadgeCheck',     v: 'ISO 9001:2015', l: 'Certified QMS',         tone: 'green' },
              { ic: 'ShieldCheck',    v: 'NDA-Protected', l: 'Secured pipeline',      tone: 'orange' },
              { ic: 'Timer',          v: '24-48 hr',      l: 'Typical TAT',           tone: 'green' },
              { ic: 'PiggyBank',      v: 'Save 54%',      l: 'vs in-house cost',      tone: 'orange' },
              { ic: 'Languages',      v: '20 Languages',  l: 'RTL-ready SEO',         tone: 'green' },
              { ic: 'Headset',        v: '24/7 Support',  l: 'Around the clock',      tone: 'orange' },
              { ic: 'ListChecks',     v: '3-Stage QC',    l: 'Every deliverable',     tone: 'green' },
              { ic: 'Users',          v: '350+ Engineers',l: 'Multi-disciplinary',    tone: 'orange' },
              { ic: 'Globe2',         v: '40+ Countries', l: 'Continuous delivery',   tone: 'green' },
              { ic: 'CalendarCheck',  v: 'On-Time',       l: '99% milestone hits',    tone: 'orange' },
              { ic: 'Wallet',         v: 'Zero Setup',    l: 'No onboarding fee',     tone: 'green' },
              { ic: 'Sparkles',       v: 'Cultural Fit',  l: 'Your culture, our team',tone: 'orange' }
            ].map(h => {
              const Ic = Icons[h.ic] || Icons.Sparkles;
              const isGreen = h.tone === 'green';
              return (
                <div
                  key={h.v}
                  data-testid={`global-highlight-${h.v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}
                  className={`group bg-white border rounded-lg p-4 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${isGreen ? 'border-gray-200 hover:border-green-500' : 'border-orange-200 hover:border-orange-500'}`}
                >
                  <div className={`w-10 h-10 rounded flex items-center justify-center mb-2 ${isGreen ? 'bg-gray-100 text-green-700' : 'bg-orange-100 text-orange-600'} group-hover:scale-110 transition-transform`}>
                    <Ic className="w-5 h-5" />
                  </div>
                  <div className="font-black text-[#0e1116] text-[14px] leading-tight">{h.v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{h.l}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/get-quote" data-testid="global-cta-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider transition-colors">
              Get a free quote <Sparkles className="w-3.5 h-3.5" />
            </Link>
            <Link to="/why-us" data-testid="global-cta-why" className="inline-flex items-center gap-2 border-2 border-[#0e1116] hover:bg-[#0e1116] hover:text-white text-[#0e1116] font-bold px-6 py-3 rounded uppercase text-xs tracking-wider transition-colors">
              Why choose us →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalReachSection;
